import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, CheckCircle2, Shield, ArrowRight, GraduationCap, Laptop, Activity, Users, Star, Award, Crown, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
const fadeIn = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    duration: 0.6
  }
};
export function Donate() {
  const [amount, setAmount] = useState<number>(100);
  const [isMonthly, setIsMonthly] = useState(false);
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const faqs = [{
    q: 'Is my donation tax-deductible?',
    a: 'Yes, Poch Global is a registered 501(c)(3) nonprofit organization. All donations are tax-deductible to the extent allowed by law. You will receive a receipt for your records.'
  }, {
    q: 'How quickly will my donation be used?',
    a: 'We deploy funds on a rolling basis. Typically, your donation is put to work within 30-60 days, directly supporting active programs and immediate community needs.'
  }, {
    q: 'Can I donate anonymously?',
    a: 'Yes. When you proceed to the payment page, you will have the option to mark your donation as anonymous, meaning your name will not appear on any public donor lists.'
  }, {
    q: 'Are there other ways to give?',
    a: 'Absolutely! You can support us by volunteering your time, shopping in our store, or setting up a peer-to-peer fundraising campaign.'
  }];
  return <div className="pt-0 bg-warmwhite" data-id="element-555" data-mp="1oc4luzk7zd1d">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-navy overflow-hidden" data-id="element-556" data-mp="1jdtbmnq0j0im">
        <div className="absolute inset-0 z-0" data-id="element-557" data-mp="1fczpnzw4rvsd">
          <img src="https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?q=80&w=2940&auto=format&fit=crop" alt="Donate" className="w-full h-full object-cover opacity-30" data-id="element-558" data-mp="1feanzk6t4bc3" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/80 to-transparent" data-id="element-559" data-mp="15gwlyplmrp3b" />
        </div>
        <div className="container mx-auto px-6 md:px-12 relative z-10 text-center max-w-4xl" data-id="element-560" data-mp="vtk9b8i92izi">
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mb-6" data-id="element-561" data-mp="10nxyuua8dvw9">
            Make an Impact
          </h1>
          <p className="text-xl text-white/80 leading-relaxed" data-id="element-562" data-mp="qrl55weori1i">
            Your donation directly supports scholarships, youth programs, and
            community outreach. Together, we're connecting Africa with the
            world.
          </p>
        </div>
      </section>

      {/* Why Donate? */}
      <section className="py-16 lg:py-24 bg-white border-b border-softgray" data-id="element-563" data-mp="14sfhur8ro55z">
        <div className="container mx-auto px-4 sm:px-6 md:px-12" data-id="element-564" data-mp="19qz3eqjg7ext">
          <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16" data-id="element-565" data-mp="19p5x5ccqywqc">
            <h2 className="text-3xl lg:text-4xl font-serif font-bold text-navy mb-4" data-id="element-566" data-mp="1xfi9r0ixthn9">Your Impact</h2>
            <p className="text-base lg:text-lg text-mediumgray leading-relaxed" data-id="element-567" data-mp="2281q2590eqvr">
              Every dollar you donate goes directly to empowering individuals and communities across Africa. Whether you're supporting a scholarship, funding a training program, or enabling community outreach, your contribution makes a tangible difference.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8" data-id="element-568" data-mp="3rnvlwtbk0lna">
            {[{
            title: 'Fund a Scholarship',
            amount: '$500',
            icon: GraduationCap,
            desc: 'Provides full tuition and materials for one student for a year.'
          }, {
            title: 'Support Tech Training',
            amount: '$250',
            icon: Laptop,
            desc: 'Equips a classroom with necessary software and hardware.'
          }, {
            title: 'Health Awareness',
            amount: '$100',
            icon: Activity,
            desc: 'Funds a community health seminar for 50+ individuals.'
          }, {
            title: 'Community Outreach',
            amount: '$50',
            icon: Users,
            desc: 'Provides essential resources for a family in need.'
          }].map((item, i) => <motion.div key={i} variants={fadeIn} initial="initial" whileInView="whileInView" className="bg-warmwhite p-8 rounded-3xl border border-softgray text-center hover:border-gold/30 hover:shadow-premium transition-all duration-300 group" data-id="element-569" data-mp="1jiwre5s9rhka" data-mpi={i}>
                <div className="w-16 h-16 mx-auto bg-white text-gold rounded-full flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform" data-id="element-570" data-mp="1xfjqae3s8hsu">
                  <item.icon className="w-8 h-8" data-id="element-571" data-mp="26grm8pitt6jr" />
                </div>
                <h3 className="text-xl font-bold text-navy mb-2" data-id="element-572" data-mp="2b8ria6vrzfb2">{item.title}</h3>
                <div className="text-2xl font-bold text-gold mb-4" data-id="element-573" data-mp="2g8nl42sql0p1">{item.amount}</div>
                <p className="text-sm text-mediumgray" data-id="element-574" data-mp="2l7753xnrs2er">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Donation Experience */}
      <section className="py-16 lg:py-24 relative overflow-hidden px-4 sm:px-6" data-id="element-575" data-mp="2q3xpuyairm6i">
        <div className="container mx-auto relative z-10 max-w-6xl" data-id="element-576" data-mp="egvhlaflhf5i">
          <div className="bg-white rounded-[2rem] lg:rounded-[2.5rem] shadow-premium overflow-hidden flex flex-col lg:flex-row" data-id="element-577" data-mp="9j2cyltxwt7c">
            {/* Left Side: Impact */}
            <div className="w-full lg:w-5/12 bg-navy p-8 sm:p-10 lg:p-12 text-white flex flex-col justify-between relative overflow-hidden" data-id="element-578" data-mp="3vrlh19jg7hnm">
              <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]" data-id="element-579" data-mp="x8wpf2ackfr2" />
              <div className="absolute top-0 right-0 w-48 h-48 lg:w-64 lg:h-64 bg-gold/10 rounded-full blur-[60px] lg:blur-[80px] pointer-events-none" data-id="element-580" data-mp="scphx52ayw70" />
              <div className="relative z-10" data-id="element-581" data-mp="ijibrhzqnc5v">
                <Heart className="w-10 h-10 lg:w-12 lg:h-12 text-white mb-6 lg:mb-8" data-id="element-582" data-mp="iipdl5euhuzc" />
                <h3 className="text-3xl lg:text-4xl text-white font-serif font-bold mb-4 lg:mb-6" data-id="element-583" data-mp="1g20myyqorcqy">
                  Your impact is immediate.
                </h3>
                <p className="text-white/90 mb-8 lg:mb-10 text-base lg:text-lg leading-relaxed" data-id="element-584" data-mp="35ie0d2eyxtvz">
                  Every dollar you donate goes directly to empowering
                  individuals and communities across Africa.
                </p>

                <div className="space-y-4 lg:space-y-6" data-id="element-585" data-mp="30qcsdsou2pbr">
                  <div className="bg-white/10 backdrop-blur-md p-4 lg:p-5 rounded-2xl border border-white/20" data-id="element-586" data-mp="30p1m9m7x15hh">
                    <div className="text-white font-bold text-xl lg:text-2xl mb-1" data-id="element-587" data-mp="2mpa2t5ymqww3">
                      $50
                    </div>
                    <div className="text-xs lg:text-sm text-white/90" data-id="element-588" data-mp="2csdvv4odqitn">
                      Provides clean water access for a family.
                    </div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-md p-4 lg:p-5 rounded-2xl border border-white/20" data-id="element-589" data-mp="2htbjvxw2m732">
                    <div className="text-white font-bold text-xl lg:text-2xl mb-1" data-id="element-590" data-mp="g03svr73i11c">
                      $100
                    </div>
                    <div className="text-xs lg:text-sm text-white/90" data-id="element-591" data-mp="20j7movuo70qg">
                      Supplies a classroom with learning materials.
                    </div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-md p-4 lg:p-5 rounded-2xl border border-white/20" data-id="element-592" data-mp="2a87nmksqg67a">
                    <div className="text-white font-bold text-xl lg:text-2xl mb-1" data-id="element-593" data-mp="25g7t57vfm5i3">
                      $500
                    </div>
                    <div className="text-xs lg:text-sm text-white/90" data-id="element-594" data-mp="2jaqoizcyk8qw">
                      Funds a full tech scholarship for a youth.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side: Form */}
            <div className="w-full lg:w-7/12 p-6 sm:p-10 lg:p-16" data-id="element-595" data-mp="2o8a2rz9noliu">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 lg:mb-10 gap-4" data-id="element-596" data-mp="ys0ykg64xyqn">
                <h3 className="text-2xl lg:text-3xl font-serif font-bold text-navy" data-id="element-597" data-mp="tv0z4o3sju8i">
                  Choose Amount
                </h3>
                <div className="flex bg-lightgray p-1 rounded-full w-full sm:w-auto" data-id="element-598" data-mp="7ypm4odemi8l">
                  <button onClick={() => setIsMonthly(false)} className={`flex-1 sm:flex-none px-4 lg:px-6 py-2 lg:py-2.5 rounded-full text-xs lg:text-sm font-bold transition-all ${!isMonthly ? 'bg-white shadow-sm text-navy' : 'text-mediumgray hover:text-navy'}`} data-id="element-599" data-mp="cxk4xqbdg6sp">
                    Give Once
                  </button>
                  <button onClick={() => setIsMonthly(true)} className={`flex-1 sm:flex-none px-4 lg:px-6 py-2 lg:py-2.5 rounded-full text-xs lg:text-sm font-bold transition-all ${isMonthly ? 'bg-white shadow-sm text-navy' : 'text-mediumgray hover:text-navy'}`} data-id="element-600" data-mp="3q6otg9m94539">
                    Monthly
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 lg:gap-4 mb-6 lg:mb-8" data-id="element-601" data-mp="1eip8d92wjubi">
                {[50, 100, 250, 500, 1000].map(val => <button key={val} onClick={() => setAmount(val)} className={`py-3 lg:py-5 rounded-2xl border-2 font-bold text-lg lg:text-xl transition-all ${amount === val ? 'border-gold bg-gold/5 text-gold' : 'border-softgray text-mediumgray hover:border-gold/30 hover:bg-warmwhite'}`} data-id="element-602" data-mp="2uy4i4vkns5yh">
                    ${val}
                  </button>)}
                <div className="relative col-span-2 sm:col-span-1" data-id="element-603" data-mp="2bb6tqlck97q3">
                  <span className="absolute left-4 lg:left-5 top-1/2 -translate-y-1/2 text-mediumgray font-bold text-lg lg:text-xl" data-id="element-604" data-mp="278a5of494jer">
                    $
                  </span>
                  <input type="number" placeholder="Custom" className="w-full h-full py-3 lg:py-5 pl-8 lg:pl-10 pr-3 lg:pr-4 rounded-2xl border-2 border-softgray focus:border-gold focus:ring-0 outline-none text-lg lg:text-xl font-bold text-charcoal transition-all" data-id="element-605" data-mp="5gdxnfhalroq" />
                </div>
              </div>

              <div className="mb-6 lg:mb-8" data-id="element-606" data-mp="1e9oor8y9rphw">
                <label className="block text-xs lg:text-sm font-bold text-mediumgray mb-2 lg:mb-3" data-id="element-607" data-mp="1e8n3ycrgbomz">
                  Direct my donation to:
                </label>
                <select className="w-full px-4 lg:px-5 py-3 lg:py-4 rounded-xl border-2 border-softgray focus:outline-none focus:border-gold text-mediumgray font-medium appearance-none bg-white text-sm lg:text-base" data-id="element-608" data-mp="ljpwl4os3c90">
                  <option data-id="element-609" data-mp="vb2ofwaku2f1">Area of Greatest Need</option>
                  <option data-id="element-610" data-mp="qjudgvtlm8zr">Education & Scholarships</option>
                  <option data-id="element-611" data-mp="qi9ij53dgo1d">Health & Wellness Programs</option>
                  <option data-id="element-612" data-mp="2t85t5niaqqa">Community Outreach</option>
                </select>
              </div>

              <button className="w-full py-4 lg:py-5 bg-gold hover:bg-gold-light text-white rounded-2xl font-bold text-lg lg:text-xl transition-all shadow-lg flex items-center justify-center gap-2 mb-4 lg:mb-6" data-id="element-613" data-mp="3u12sz90uswrb">
                Continue to Payment <ArrowRight className="w-4 h-4 lg:w-5 lg:h-5" data-id="element-614" data-mp="2eajpgvwz7s0z" />
              </button>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-xs lg:text-sm text-mediumgray font-medium" data-id="element-615" data-mp="20ivfay6afdmn">
                <span className="flex items-center gap-2" data-id="element-616" data-mp="2t69gj8h0qvck">
                  <Shield className="w-3 h-3 lg:w-4 lg:h-4 text-gold" data-id="element-617" data-mp="2t6rb80o191id" /> Secure Payment
                </span>
                <span className="flex items-center gap-2" data-id="element-618" data-mp="2x71qy51mn6af">
                  <CheckCircle2 className="w-3 h-3 lg:w-4 lg:h-4 text-gold" data-id="element-619" data-mp="2j8uzb6n9u2j7" /> Tax Deductible
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Transparency & Impact */}
      <section className="py-16 lg:py-24 bg-white border-y border-softgray" data-id="element-620" data-mp="17rbtd6ib3lgy">
        <div className="container mx-auto px-4 sm:px-6 md:px-12" data-id="element-621" data-mp="1ct12gglyxp2e">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16" data-id="element-622" data-mp="spn5v6hb1iq5">
            <motion.div variants={fadeIn} initial="initial" whileInView="whileInView" className="w-full lg:w-1/2 order-2 lg:order-1" data-id="element-623" data-mp="srpvvqigjbuv">
              <h2 className="text-3xl lg:text-4xl font-serif font-bold text-navy mb-4 lg:mb-6" data-id="element-624" data-mp="eyhz7kfirq6b">Transparency & Impact</h2>
              <p className="text-base lg:text-lg text-mediumgray mb-8 lg:mb-10 leading-relaxed" data-id="element-625" data-mp="9zx0xqjtvgki">
                We believe in complete transparency. Here is exactly how your donations are allocated to maximize impact across our various initiatives.
              </p>
              
              <div className="space-y-6 lg:space-y-8" data-id="element-626" data-mp="3s59z57q4yfuu">
                <div data-id="element-627" data-mp="3s6l61gevo3q6">
                  <div className="flex justify-between text-xs lg:text-sm font-bold text-navy mb-2" data-id="element-628" data-mp="3s7cywjh3bncr">
                    <span data-id="element-629" data-mp="1vaduucck0esr">Scholarships & Education</span>
                    <span className="text-gold" data-id="element-630" data-mp="1qeykvvho331m">40%</span>
                  </div>
                  <div className="w-full bg-warmwhite rounded-full h-2 lg:h-3 border border-softgray overflow-hidden" data-id="element-631" data-mp="1xxy0rr5x6cg8">
                    <motion.div initial={{
                    width: 0
                  }} whileInView={{
                    width: '40%'
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 1,
                    delay: 0.2
                  }} className="bg-gold h-full rounded-full" data-id="element-632" data-mp="22uy6u22rrf7c" />
                  </div>
                </div>
                <div data-id="element-633" data-mp="1jwvebdb0j04s">
                  <div className="flex justify-between text-xs lg:text-sm font-bold text-navy mb-2" data-id="element-634" data-mp="1jy6hpc0jq5vd">
                    <span data-id="element-635" data-mp="1ovoujdhop58n">Health Programs</span>
                    <span className="text-gold" data-id="element-636" data-mp="1sy3anptyxqw8">30%</span>
                  </div>
                  <div className="w-full bg-warmwhite rounded-full h-2 lg:h-3 border border-softgray overflow-hidden" data-id="element-637" data-mp="1svyyylptvnle">
                    <motion.div initial={{
                    width: 0
                  }} whileInView={{
                    width: '30%'
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 1,
                    delay: 0.4
                  }} className="bg-gold h-full rounded-full" data-id="element-638" data-mp="34d8fazdntzih" />
                  </div>
                </div>
                <div data-id="element-639" data-mp="3cvk7w7cpwqyf">
                  <div className="flex justify-between text-xs lg:text-sm font-bold text-navy mb-2" data-id="element-640" data-mp="37v5x4hjqf2wp">
                    <span data-id="element-641" data-mp="3luxgkxt0pbi3">Community Outreach</span>
                    <span className="text-gold" data-id="element-642" data-mp="3ltmcewvnu1qg">20%</span>
                  </div>
                  <div className="w-full bg-warmwhite rounded-full h-2 lg:h-3 border border-softgray overflow-hidden" data-id="element-643" data-mp="3gwm56in2qsxr">
                    <motion.div initial={{
                    width: 0
                  }} whileInView={{
                    width: '20%'
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 1,
                    delay: 0.6
                  }} className="bg-gold h-full rounded-full" data-id="element-644" data-mp="3vjfgzdxdxgl7" />
                  </div>
                </div>
                <div data-id="element-645" data-mp="n2hqzz2q6e0x">
                  <div className="flex justify-between text-xs lg:text-sm font-bold text-navy mb-2" data-id="element-646" data-mp="n416hngk4l1n">
                    <span data-id="element-647" data-mp="s0jc0pjponf1">Operations & Admin</span>
                    <span className="text-mediumgray" data-id="element-648" data-mp="e21n4oi2l4vj">10%</span>
                  </div>
                  <div className="w-full bg-warmwhite rounded-full h-2 lg:h-3 border border-softgray overflow-hidden" data-id="element-649" data-mp="e4e2ctwg114w">
                    <motion.div initial={{
                    width: 0
                  }} whileInView={{
                    width: '10%'
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 1,
                    delay: 0.8
                  }} className="bg-navy h-full rounded-full" data-id="element-650" data-mp="4ci33fimf4z3" />
                  </div>
                </div>
              </div>
              
              <button className="mt-8 lg:mt-10 px-6 lg:px-8 py-3 lg:py-4 bg-warmwhite border border-softgray text-navy hover:border-navy rounded-full font-bold transition-all text-sm lg:text-base w-full sm:w-auto" data-id="element-651" data-mp="3hi8i86mf5tsp">
                View Detailed Financial Report
              </button>
            </motion.div>
            
            <motion.div variants={fadeIn} initial="initial" whileInView="whileInView" className="w-full lg:w-1/2 order-1 lg:order-2" data-id="element-652" data-mp="1n9v62dtsypso">
              <div className="rounded-[2rem] overflow-hidden shadow-2xl border border-softgray" data-id="element-653" data-mp="1ebj81qqlwpq7">
                <img src="https://images.unsplash.com/photo-1531206715517-5c0ba140b2b8?q=80&w=2940&auto=format&fit=crop" alt="Impact" className="w-full h-[300px] sm:h-[400px] lg:h-[500px] object-cover" data-id="element-654" data-mp="2az7f9dp4qxuz" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Donor Recognition */}
      <section className="py-12 lg:py-16 bg-warmwhite" data-id="element-655" data-mp="1x0hiy0a4x27o">
        <div className="container mx-auto px-6 md:px-12" data-id="element-656" data-mp="qkxcgyxsgxx3">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-657" data-mp="qiaus6h6p215">
            <h2 className="text-4xl font-serif font-bold text-navy mb-4" data-id="element-658" data-mp="lksqwui0xd00">Recognition & Benefits</h2>
            <p className="text-lg text-mediumgray" data-id="element-659" data-mp="bj7065yi64vy">
              We deeply appreciate our supporters. Join a membership tier and receive exclusive updates and benefits.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-660" data-mp="le8q340lrazl">
            {[{
            title: 'Friend',
            amount: '$25+',
            icon: Heart,
            benefits: ['Newsletter subscription', 'Quarterly impact updates']
          }, {
            title: 'Supporter',
            amount: '$50+',
            icon: Star,
            benefits: ['All Friend benefits', 'Annual impact report', 'Website recognition']
          }, {
            title: 'Champion',
            amount: '$100+',
            icon: Award,
            benefits: ['All Supporter benefits', 'Exclusive event invites', 'Named scholarship option']
          }, {
            title: 'Founder',
            amount: '$500+',
            icon: Crown,
            benefits: ['All Champion benefits', 'Quarterly board updates', 'Annual dinner invitation']
          }].map((tier, i) => <motion.div key={i} variants={fadeIn} initial="initial" whileInView="whileInView" className="bg-white p-8 rounded-3xl border border-softgray shadow-sm flex flex-col" data-id="element-661" data-mp="3ayltffwb7r6v" data-mpi={i}>
                <div className="w-12 h-12 bg-gold/10 text-gold rounded-full flex items-center justify-center mb-6" data-id="element-662" data-mp="1qdx52lmf78jw">
                  <tier.icon className="w-6 h-6" data-id="element-663" data-mp="1lhzp4gf2k47n" />
                </div>
                <h3 className="text-2xl font-bold text-navy mb-1" data-id="element-664" data-mp="xqktt7lpmn5w">{tier.title}</h3>
                <div className="text-xl font-bold text-gold mb-6" data-id="element-665" data-mp="srzt772jq1gx">{tier.amount}</div>
                <ul className="space-y-3 mb-8 flex-grow" data-id="element-666" data-mp="16qzhueq26gd3">
                  {tier.benefits.map((benefit, idx) => <li key={idx} className="flex items-start gap-2 text-sm text-mediumgray" data-id="element-667" data-mp="12ocmi3u6idb5" data-mpi={idx}>
                      <CheckCircle2 className="w-4 h-4 text-gold shrink-0 mt-0.5" data-id="element-668" data-mp="2i4utvelo2903" />
                      {benefit}
                    </li>)}
                </ul>
                <button className="w-full py-3 bg-warmwhite hover:bg-navy hover:text-white text-navy font-bold rounded-xl transition-colors border border-softgray hover:border-navy" data-id="element-669" data-mp="2lnag4gok0wxb">
                  Choose Level
                </button>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 lg:py-16 bg-navy text-white relative overflow-hidden" data-id="element-670" data-mp="2gqtj0wzdn883">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]" data-id="element-671" data-mp="34ethyk6ue4xr" />
        <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-672" data-mp="39f8uxan7paf7">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-673" data-mp="2unyuxdlrv1b9">
            <h2 className="text-4xl text-white font-serif font-bold mb-4" data-id="element-674" data-mp="2zmtcydbx0lub">See what your donations accomplish</h2>
            <p className="text-lg text-white/70" data-id="element-675" data-mp="3nc4gg2knguc5">
              Real stories from the individuals and communities you support.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-676" data-mp="1njm987u6veml">
            <motion.div variants={fadeIn} initial="initial" whileInView="whileInView" className="bg-white/10 backdrop-blur-md p-8 rounded-3xl border border-white/20" data-id="element-677" data-mp="1nkdzr48xim60">
              <div className="flex gap-1 text-gold mb-6" data-id="element-678" data-mp="1dsrl0xxdu4yi">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" data-id="element-679" data-mp="1is4375db6nur" data-mpi={i} />)}
              </div>
              <p className="text-lg text-white/90 leading-relaxed mb-8 italic" data-id="element-680" data-mp="14sl9vip5ph2i">
                "The tech scholarship provided by Poch Global completely changed my trajectory. I now have the skills to support my family and build a career I am passionate about."
              </p>
              <div className="flex items-center gap-4" data-id="element-681" data-mp="zu083eu9apby">
                <img src="https://images.unsplash.com/photo-1531123897727-8f129e1bf98c?q=80&w=2787&auto=format&fit=crop" alt="Student" className="w-14 h-14 rounded-full object-cover border-2 border-gold" data-id="element-682" data-mp="zujqewntjfqi" />
                <div data-id="element-683" data-mp="2u7j3ai0tdvu3">
                  <div className="font-bold text-white" data-id="element-684" data-mp="2u5oqxs3lzc1m">David O.</div>
                  <div className="text-sm text-gold" data-id="element-685" data-mp="il7d486nkioc">Scholarship Recipient</div>
                </div>
              </div>
            </motion.div>
            
            <motion.div variants={fadeIn} initial="initial" whileInView="whileInView" className="bg-white/10 backdrop-blur-md p-8 rounded-3xl border border-white/20" data-id="element-686" data-mp="sb0p3aapizt4">
              <div className="flex gap-1 text-gold mb-6" data-id="element-687" data-mp="nhxt6vigje9r">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" data-id="element-688" data-mp="119cg3dfhjzm3" data-mpi={i} />)}
              </div>
              <p className="text-lg text-white/90 leading-relaxed mb-8 italic" data-id="element-689" data-mp="wbkji63wbpbk">
                "The health awareness seminars funded by donors have brought critical knowledge to our village. We are healthier, more informed, and deeply grateful."
              </p>
              <div className="flex items-center gap-4" data-id="element-690" data-mp="3czy76962uhty">
                <img src="https://images.unsplash.com/photo-1531384441138-2736e62e0919?q=80&w=2787&auto=format&fit=crop" alt="Community Leader" className="w-14 h-14 rounded-full object-cover border-2 border-gold" data-id="element-691" data-mp="3d2kpn3uiahqy" />
                <div data-id="element-692" data-mp="5lzy25q7oxjx">
                  <div className="font-bold text-white" data-id="element-693" data-mp="5l7yykxeohow">Amina T.</div>
                  <div className="text-sm text-gold" data-id="element-694" data-mp="al2q8pl3f9ah">Community Leader</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 lg:py-16 bg-white border-b border-softgray" data-id="element-695" data-mp="kj8rr62zmdet">
        <div className="container mx-auto px-6 md:px-12 max-w-3xl" data-id="element-696" data-mp="kh63orleaucq">
          <div className="text-center mb-16" data-id="element-697" data-mp="ojj739co6g6c">
            <h2 className="text-4xl font-serif font-bold text-navy mb-4" data-id="element-698" data-mp="tbkfulamporm">Donation FAQs</h2>
            <p className="text-lg text-mediumgray" data-id="element-699" data-mp="tdwvuswttp21">Common questions about giving to Poch Global.</p>
          </div>
          
          <div className="space-y-4" data-id="element-700" data-mp="13a04mhm6olxy">
            {faqs.map((faq, i) => <div key={i} className="bg-warmwhite border border-softgray rounded-2xl overflow-hidden" data-id="element-701" data-mp="137x8dfuftwks" data-mpi={i}>
                <button onClick={() => setActiveFaq(activeFaq === i ? null : i)} className="w-full px-6 py-5 flex items-center justify-between hover:bg-lightgray transition-colors text-left" data-id="element-702" data-mp="2sqvqqhebf9rs">
                  <span className="font-bold text-navy" data-id="element-703" data-mp="2ithbum81it1l">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-mediumgray transition-transform duration-300 shrink-0 ${activeFaq === i ? 'rotate-180' : ''}`} data-id="element-704" data-mp="2du4p01h65lz0" />
                </button>
                <AnimatePresence data-id="element-705" data-mp="36iabasnvfj0y">
                  {activeFaq === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-706" data-mp="36j24xxxwr6ol">
                      <div className="px-6 pb-5 text-mediumgray border-t border-softgray pt-4 leading-relaxed" data-id="element-707" data-mp="3u9fqd5lzm7a7">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* Other Ways to Help */}
      <section className="py-12 lg:py-16 bg-warmwhite text-center" data-id="element-708" data-mp="301gv35l463tv">
        <div className="container mx-auto px-6" data-id="element-709" data-mp="303bjwuziag38">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy mb-8" data-id="element-710" data-mp="3513ddtfsua9j">
            Other Ways to Help
          </h2>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-711" data-mp="3sqebt49ofski">
            <Link to="/volunteer" className="px-8 py-4 bg-navy text-white hover:bg-navy-light rounded-full font-bold transition-all shadow-lg hover:-translate-y-1" data-id="element-712" data-mp="1ia0zjyrbrgy">
              Volunteer
            </Link>
            <Link to="/shop" className="px-8 py-4 bg-white border border-softgray text-navy hover:border-navy rounded-full font-bold transition-all shadow-sm hover:-translate-y-1" data-id="element-713" data-mp="3nxl90lxrqub2">
              Shop & Support
            </Link>
            <Link to="/contact" className="px-8 py-4 bg-white border border-softgray text-navy hover:border-navy rounded-full font-bold transition-all shadow-sm hover:-translate-y-1" data-id="element-714" data-mp="kbw3d1ftqb0d">
              Partner With Us
            </Link>
            <button className="px-8 py-4 bg-gold text-white hover:bg-gold-light rounded-full font-bold transition-all shadow-lg hover:-translate-y-1" data-id="element-715" data-mp="2eprowtaav3cc">
              Fundraise
            </button>
          </div>
        </div>
      </section>
    </div>;
}